import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "frame", "input", "spinner"]

  connect() {
    this.boundEnsureOffset = this.ensureOverlayOffset.bind(this)
    this.boundHandleLoading = this.handleLoadingEvent.bind(this)
    window.addEventListener("resize", this.boundEnsureOffset)
    window.addEventListener("header-search:loading", this.boundHandleLoading)
    this.scrollLocked = false
    this.setupFrameObserver()
    this.hideOverlay(true)
  }

  disconnect() {
    window.removeEventListener("resize", this.boundEnsureOffset)
    window.removeEventListener("header-search:loading", this.boundHandleLoading)
    if (this.frameObserver) {
      this.frameObserver.disconnect()
      this.frameObserver = null
    }
    this.unlockScroll()
  }

  show() {
    if (!this.hasOverlayTarget) return
    this.ensureOverlayOffset()
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.remove("opacity-0")
    this.overlayTarget.classList.add("opacity-100")
    this.lockScroll()
    this.hideSpinner()
  }

  hide() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("opacity-100")
    this.overlayTarget.classList.add("opacity-0")
    this.overlayTarget.addEventListener(
      "transitionend",
      () => {
        if (this.overlayTarget.classList.contains("opacity-0")) {
          this.overlayTarget.classList.add("hidden")
          this.unlockScroll()
        }
      },
      { once: true }
    )
  }

  maybeHide(event) {
    if (!this.hasOverlayTarget) return
    const value = (event.target || this.inputTarget)?.value.toString().trim()
    if (value === "") {
      this.hideOverlay(false)
    }
  }

  hideOverlay(immediate = false) {
    if (!this.hasOverlayTarget) return
    if (immediate instanceof Event) {
      immediate.preventDefault()
      immediate = false
    }

    if (immediate) {
      this.overlayTarget.classList.add("hidden")
      this.overlayTarget.classList.add("opacity-0")
      this.overlayTarget.classList.remove("opacity-100")
      this.unlockScroll()
      this.hideSpinner()
      return
    }

    this.hide()
  }

  ensureOverlayOffset() {
    if (!this.hasOverlayTarget) return
    const isMobile = window.matchMedia("(max-width: 1023px)").matches
    const offset = isMobile ? 0 : this.element.getBoundingClientRect().bottom
    this.overlayTarget.style.setProperty("--search-overlay-offset", `${offset}px`)
  }

  lockScroll() {
    if (this.scrollLocked) return
    this.originalBodyOverflow = document.body.style.overflow
    this.originalHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = "hidden"
    document.documentElement.style.overflow = "hidden"
    this.scrollLocked = true
  }

  unlockScroll() {
    if (!this.scrollLocked) return
    document.body.style.overflow = this.originalBodyOverflow || ""
    document.documentElement.style.overflow = this.originalHtmlOverflow || ""
    this.scrollLocked = false
  }

  handleLoadingEvent() {
    if (this.inputTarget?.value?.trim()) {
      this.showSpinner()
    }
  }

  setupFrameObserver() {
    if (!this.hasFrameTarget) return
    this.frameObserver = new MutationObserver(() => {
      this.hideSpinner()
    })
    this.frameObserver.observe(this.frameTarget, { childList: true })
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
  }
}
